import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getCustomerAccountDetails, CustomerAccountDetailsData } from "../../services/api/user.service";
import { toast } from "react-toastify";
import { ArrowLeft } from "lucide-react";

interface CustomerProfileProps {
  customerId: string;
}

const CustomerProfile: React.FC<CustomerProfileProps> = ({ customerId }) => {
  const navigate = useNavigate();
  const [customerData, setCustomerData] = useState<CustomerAccountDetailsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCustomerProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const userId = parseInt(customerId, 10);
        
        if (isNaN(userId)) {
          setError("Invalid customer ID");
          toast.error("Invalid customer ID");
          return;
        }
        
        const data = await getCustomerAccountDetails(userId);
        setCustomerData(data);
      } catch (err: any) {
        console.error("Error fetching customer profile:", err);
        setError(err.message || "Failed to fetch customer profile");
        toast.error(err.message || "Failed to fetch customer profile");
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchCustomerProfile();
    }
  }, [customerId]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number | string, currency: string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(numAmount);
  };

  if (loading) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-gray-600 font-family-messina-sans">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !customerData) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <button
            onClick={() => navigate("/customer-details")}
            className="mb-4 flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors font-family-messina-sans"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Customer Details</span>
          </button>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-red-600 font-family-messina-sans">{error || "Customer not found"}</div>
          </div>
        </div>
      </div>
    );
  }

  const accounts = customerData.accounts || [];
  const artistName = customerData.artistNames && customerData.artistNames.length > 0 
    ? customerData.artistNames[0] 
    : "";

  return (
    <div className="bg-nova-dashboard-900 min-h-screen">
      {/* Main Content Area */}
      <div className="p-4 sm:p-6 md:p-8">
        {/* Header with Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/customer-details")}
            className="mb-4 flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors font-family-messina-sans"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Customer Details</span>
          </button>
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern">Customer Info </span>
        </div>

        {/* Account Information Section */}
        <div className="mb-6 sm:mb-8 mt-[3rem]">
          <span className="text-[1.4375rem] font-regular text-gray-900 mb-6 block font-family-messina-modern">Account Information</span>
          
          <div className="space-y-4 sm:space-y-6 font-family-messina-sans-regular">
            {/* Artist Name */}
            <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                ARTIST NAME
              </label>
              <input
                type="text"
                value={artistName || "N/A"}
                readOnly
                className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
              />
            </div>

            {/* Full Name */}
            <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                FULL NAME
              </label>
              <input
                type="text"
                value={
                  customerData.user?.firstname || customerData.user?.lastname
                    ? `${customerData.user?.firstname || ''} ${customerData.user?.lastname || ''}`.trim()
                    : "N/A"
                }
                readOnly
                className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
              />
            </div>
          </div>
        </div>

        {/* Accounts Section */}
        {accounts.length > 0 && (
          <div className="mb-6 sm:mb-8 mt-[4rem]">
            <span className="text-[1.4375rem] font-regular text-gray-900 mb-6 block font-family-messina-modern">Accounts</span>
            
            <div className="space-y-4 sm:space-y-6 font-family-messina-sans-regular">
              {accounts.map((account, index) => (
                <div key={`${account.accountId}-${index}`} className="space-y-4">
                  {/* Account Number */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      ACCOUNT NUMBER
                    </label>
                    <input
                      type="text"
                      value={account.accountNumber || "N/A"}
                      readOnly
                      className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
                    />
                  </div>

                  {/* Routing Number */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      ROUTING NUMBER
                    </label>
                    <input
                      type="text"
                      value={account.routingNumber || "N/A"}
                      readOnly
                      className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
                    />
                  </div>

                  {/* Account Status */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      ACCOUNT STATUS
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={account.accountStatus || ""}
                        readOnly
                        className="flex-1 text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
                      />
                      <span className={`inline-block px-2 py-1 rounded text-xs ${
                        account.accountStatus === "Open" ? "bg-green-100 text-green-800" : 
                        account.accountStatus === "Closed" ? "bg-red-100 text-red-800" : 
                        "bg-gray-100 text-gray-800"
                      }`}>
                        {account.accountStatus}
                      </span>
                    </div>
                  </div>

                  {/* Balance */}
                  <div>
                    <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                      BALANCE
                    </label>
                    <input
                      type="text"
                      value={account.balance ? formatCurrency(account.balance, account.currency || "USD") : "N/A"}
                      readOnly
                      className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none pb-2 font-family-messina-sans cursor-default"
                    />
                  </div>

                  {index < accounts.length - 1 && (
                    <div className="border-b border-gray-300 my-6"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerProfile;
